import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import { resolveEnrollmentStatusKey } from "../../UsersAndGroups/config/enrollmentStatus";
import { MIXED_STATUS } from "../utils";
import { resolveScoStatus } from "../config/scoStatusConfig";
import {
  getNewStatusOptions,
  isFieldInvalid,
  StatusFormErrors,
  StatusFormField,
  StatusFormFieldKey,
  StatusFormKind,
  StatusFormValues,
  isFieldEnabled,
  isFieldRequired,
} from "../config/statusFormConfig";

const CURRENT_STATUS_CODES = ["E", "P", "C", "X"];

interface StatusFormProps {
  kind: StatusFormKind;
  fields: StatusFormField[];
  values: StatusFormValues;
  errors: StatusFormErrors;
  disabled?: boolean;
  hasExemptSelected?: boolean;
  onChange: (key: StatusFormFieldKey, value: string | null) => void;
}

const StatusForm: React.FC<StatusFormProps> = ({
  kind,
  fields,
  values,
  errors,
  disabled,
  hasExemptSelected,
  onChange,
}) => {
  const { t } = useTranslation("enrollment-progress");
  const { t: tEnroll } = useTranslation("users-and-groups");

  const statusOption = (code: string) => ({
    value: code,
    label:
      code === MIXED_STATUS
        ? String(t("form.mixed-status"))
        : String(
            tEnroll(
              `card.status.${resolveEnrollmentStatusKey({ status: code })}`,
            ),
          ),
  });

  const trackingOption = (value: string) => ({
    value,
    label: String(t(`form.tracking-status.${value}`)),
  });

  const getOptions = (field: StatusFormField) => {
    switch (field.options) {
      case "status":
        return kind === "generic"
          ? Array.from(
              new Set([...CURRENT_STATUS_CODES, values.currentStatus ?? ""]),
            )
              .filter(Boolean)
              .map(statusOption)
          : values.currentStatus
            ? [
                {
                  value: values.currentStatus,
                  label: (() => {
                    const sco = resolveScoStatus(values.currentStatus);
                    return sco
                      ? String(t(`sco-status.${sco.labelKey}`))
                      : values.currentStatus;
                  })(),
                },
              ]
            : [];
      case "newStatus":
        return getNewStatusOptions(kind, values, { hasExemptSelected }).map(
          (value) =>
            kind === "generic" ? statusOption(value) : trackingOption(value),
        );
      default:
        return [];
    }
  };

  const renderField = (field: StatusFormField) => {
    const common = {
      id: `enrollment-progress-${field.key}`,
      label: String(t(`form.fields.${field.labelKey?.(values) ?? field.key}`)),
      required: isFieldRequired(field, values),
      disabled: disabled || !isFieldEnabled(field, values),
      error: !!errors[field.key],
      errorMessage: String(
        t(
          isFieldInvalid(field, values)
            ? "form.invalid-number"
            : "form.required",
        ),
      ),
    };

    switch (field.type) {
      case "select":
        return (
          <SelectCst
            {...common}
            value={values[field.key] ?? ""}
            options={getOptions(field)}
            onChange={(value: any) => onChange(field.key, value || null)}
          />
        );
      case "date":
        return (
          <DataPickerCst
            {...common}
            value={values[field.key] ?? null}
            onChange={(value: any) =>
              onChange(
                field.key,
                value
                  ? typeof value === "string"
                    ? value
                    : (value?.toISOString?.() ?? null)
                  : null,
              )
            }
          />
        );
      default:
        return (
          <InputCst
            {...common}
            value={values[field.key] ?? ""}
            onChange={(event: any) =>
              onChange(field.key, event.target.value || null)
            }
          />
        );
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 2,
        rowGap: 2.5,
      }}
    >
      {fields.map((field) => (
        <Box
          key={field.key}
          sx={{
            minWidth: 0,
            gridColumn: field.fullWidth ? "1 / -1" : undefined,
          }}
        >
          {renderField(field)}
        </Box>
      ))}
    </Box>
  );
};

export default StatusForm;
